<template lang="html">
  <v-dialog
    scrollable
    :value="true"
    max-width="800"
    persistent
  >
    <v-card v-if="initialLoading">
      <v-card-text
        class="text-center"
      >
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        />
      </v-card-text>
    </v-card>
    <v-form
      v-else
      @submit.prevent="saveItem"
      v-model="isValid"
      ref="form"
      style="width:100%"
    >
      <v-card :class="{large: !appSmall}">
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          {{recordId === 'new' ? 'Create' : 'Edit'}} Alert
        </v-card-title>

        <v-card-text v-if="!initialLoading" class="pt-4">
          <v-layout wrap>
            <v-flex xs12 md6>
              <v-text-field
                label="Alert Name"
                outlined
                v-model="record.name"
                :disabled="loading"
                :rules="[...validation.required]"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6>
              <v-text-field
                label="Alert Description"
                outlined
                v-model="record.description"
                :disabled="loading"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs12 md6>
              <v-select
                outlined
                label="Notification Method"
                :items="notificationMethods"
                v-model="record.notification_method"
                :rules="[...validation.required]"
              />
            </v-flex>
            <v-flex xs12 md6>
              <v-checkbox
                v-model="record.enabled"
                label="Enabled"
              />
            </v-flex>
          </v-layout>
          <h2 class="display-1 mt-5 mb-3">Conditions</h2>
          <div class="table-responsive mb-4">
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Comparison</th>
                  <th>Value</th>
                  <th style="width:80px"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(condition, $index) in conditions"
                  :key="$index"
                >
                  <td>
                    <AlertConditionTypesPicker
                      :value="condition.property || ''"
                      @input="$set(condition, 'property', $event)"
                    />
                  </td>
                  <td>
                    <v-select
                      outlined
                      hide-details
                      single-line
                      v-if="operatorsForField(condition.property).length > 0"
                      :value="condition.operator || ''"
                      @input="$set(condition, 'operator', $event)"
                      :items="operatorsForField(condition.property)"
                      :disabled="loading"
                    />
                  </td>
                  <td>
                    <AlertConditionTypesPicker
                      v-if="condition.field !== 'value'"
                      :value="condition.field || ''"
                      @input="$set(condition, 'field', $event)"
                    />
                    <v-select
                      v-else-if="typeForField(condition.property) == 'boolean'"
                      outlined
                      hide-details
                      single-line
                      :value="condition.value"
                      @input="$set(condition, 'value', $event)"
                      :items="[{text: 'Yes', value: '1'}, {text: 'No', value: ''}]"
                      :disabled="loading"
                    />
                    <v-text-field
                      v-else
                      hide-details
                      single-line
                      outlined
                      :value="condition.value || ''"
                      @input="$set(condition, 'value', $event)"
                      :disabled="loading"
                    />
                  </td>
                  <td>
                    <div>
                      <v-btn
                        icon
                        @click="toggleFieldType(condition)"
                        small
                      >
                        <v-icon small>{{ condition.field === 'value' ? 'fas fa-list' : 'far fa-keyboard' }}</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :disabled="loading"
                        small
                        @click="deleteCondition(condition)"
                      >
                        <v-icon small>fa-trash-alt</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <v-btn
              v-if="!loading"
              @click="addCondition"
              color="default"
              class="ml-1 mb-1"
            >
              Add Condition
            </v-btn>
            <br />
          </div>

          <h2 class="display-1 mb-3">Dampening</h2>
          <v-layout wrap>
            <v-flex xs12>
              <v-select
                outlined
                v-model="record.dampening"
                label="Dampening Mode"
                :items="alertDampenings.map(v => ({text: v.name, value: v.id }))"
                :disabled="loading"
                :rules="[...validation.required]"
              ></v-select>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs12 md6>
              <v-text-field
                label="Occurrences"
                outlined
                v-model="record.occurrences"
                :disabled="loading"
                :rules="[...validation.required]"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6>
              <v-text-field
                label="Frequency (minutes)"
                outlined
                v-model="record.frequency"
                :disabled="loading"
                :rules="[...validation.required]"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs12 md6 class="mb-3">
              <TimePicker
                outlined
                label="Start Time"
                :value="record.schedule_start ? record.schedule_start + ':00' : ''"
                @input="$set(record, 'schedule_start', $event ? $event.split(':').slice(0, 2).join(':') : '')"
                utc
              />
            </v-flex>
            <v-flex xs12 md6 class="mb-3">
              <TimePicker
                outlined
                label="End Time"
                :value="record.schedule_end ? record.schedule_end + ':00' : ''"
                @input="$set(record, 'schedule_end', $event ? $event.split(':').slice(0, 2).join(':') : '')"
                utc
              />
            </v-flex>
          </v-layout>
          <v-layout wrap>
            <v-flex xs12>
              <WeekdaysPicker
                outlined
                multiple
                :value="record.schedule_days ? record.schedule_days.split(',') : []"
                @input="$set(record, 'schedule_days', $event.join(','))"
              />
            </v-flex>
          </v-layout>
          <h2 class="display-1 mb-3">Machines</h2>
          <MachinesPicker
            outlined
            :value="record.all_machines ? machines.map(m => m.id) : record.machines"
            @input="updateMachines"
            multiple
          />
        </v-card-text>

        <v-divider></v-divider>

        <v-alert :value="true" type="error" style="width:100%" v-if="error">{{error}}</v-alert>

        <v-card-actions>
          <v-btn
            v-if="!loading"
            @click="$emit('close')"
            text
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-progress-circular
            v-if="loading"
            class="ma-2"
            indeterminate
            color="primary"
          ></v-progress-circular>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!loading"
            color="primary"
            text
            type="submit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<style lang="scss" scoped>
.large {
  .layout {
    .flex:first-child:not(:last-child) {
      padding-right: 8px;
    }
    .flex:last-child:not(:first-child) {
      padding-left: 8px;
    }
  }
}
.table-responsive {
  overflow-y: auto;
}
table {
  min-width: 600px;
  td {
    padding-bottom: 10px;
    padding-right: 8px;
  }
  td:last-child {
    padding-right: 0;
  }
  td:last-child:first-child {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import pick from 'lodash/pick'
import get from 'lodash/get'
import ValidationMixin from '@/mixins/ValidationMixin'
import MachinesPicker from '@/components/MachinesPicker'
import WeekdaysPicker from '@/components/WeekdaysPicker'
import TimePicker from '@/components/TimePicker'
import axios from 'axios'
import AlertConditionTypesPicker from '@/components/AlertConditionTypesPicker'

export default {
  mixins: [ValidationMixin],
  components: {
    AlertConditionTypesPicker,
    MachinesPicker,
    WeekdaysPicker,
    TimePicker
  },
  props: {
    recordId: {
      type: [String, Number]
    }
  },
  data () {
    return {
      record: {
        all_machines: false,
        machines: [],
        schedule_days: ''
      },
      conditions: [],
      error: null,
      initialLoading: false,
      loading: false
    }
  },
  computed: {
    ...mapState('alertConditionTypes', {
      alertconditiontypesLoading: 'loading',
      alertConditionTypes: 'data'
    }),
    ...mapState('alertDampening', {
      alertDampeningLoading: 'loading',
      alertDampenings: 'data'
    }),
    ...mapState('alertNotifications', {
      alertNotificationsLoading: 'loading',
      alertNotifications: 'data'
    }),
    ...mapState('machines', {
      machinesLoading: 'loading',
      machines: 'data'
    }),
    notificationMethods () {
      return this.alertNotifications.map(a => ({ text: a.name, value: a.id }))
    }
  },
  methods: {
    ...mapActions('machines', {
      findMachines: 'find'
    }),
    ...mapActions('alertConditionTypes', {
      findAlertConditionTypes: 'find'
    }),
    ...mapActions('alertDampening', {
      findAlertDampening: 'find'
    }),
    ...mapActions('alertNotifications', {
      findAlertNotifications: 'find'
    }),
    ...mapActions('alerts', {
      save: 'save',
      getItem: 'get'
    }),
    typeForField (key) {
      let field = this.alertConditionTypes.find((type) => type.id === key)
      if (!field) {
        return null
      }
      return field.type
    },
    operatorsForField (key) {
      let type = this.typeForField(key)
      let operators = {
        float: ['=', '>', '>=', '<', '<='],
        integer: ['=', '>', '>=', '<', '<='],
        boolean: [],
        string: []
      }
      return operators[type || 'string'] || []
    },
    addCondition () {
      this.conditions.push({ alert: this.record.id || '', property: '', value: '', operator: '' })
    },
    toggleFieldType (condition) {
      if (condition.field !== 'value') {
        this.$set(condition, 'field', 'value')
        this.$set(condition, 'value', null)
      } else {
        if (Object.keys(condition).includes('value')) {
          delete condition.value
        }
        this.$set(condition, 'field', null)
      }
    },
    async deleteCondition (condition) {
      if (condition.id) {
        if (!window.confirm('Do you really want to delete this condition?')) {
          return
        }
        this.loading = true
        await axios({
          method: 'DELETE',
          url: '/rest/alerts/' + this.record.id + '/conditions/' + condition.id
        })
        this.loading = false
      }
      this.conditions = this.conditions.filter(c => c.id + '' !== condition.id + '')
    },
    updateMachines (value) {
      if (value && value.length && value.length === this.machines.length) {
        this.record.all_machines = true
        this.$set(this.record, 'machines', [])
      } else {
        this.record.all_machines = false
        this.$set(this.record, 'machines', value)
      }
    },
    async saveItem () {
      if (!this.isValid) {
        this.$refs.form.validate()
        return
      }
      this.loading = true
      this.error = null
      try {
        this.record = await this.save([
          pick(this.record, ['id', 'name', 'description', 'enabled', 'dampening', 'occurrences', 'frequency', 'notification_method', 'machines', 'all_machines', 'schedule_start', 'schedule_end', 'schedule_days']),
          {
            method: this.record.id ? 'PUT' : 'POST'
          }
        ])
        await Promise.all(this.conditions.map((condition) => {
          return axios({
            method: condition.id ? 'PUT' : 'POST',
            url: '/rest/alerts/' + this.record.id + '/conditions' + (condition.id ? '/' + condition.id : ''),
            data: {
              alert: this.record.id,
              property: condition.property,
              value: condition.value,
              field: condition.field,
              operator: condition.operator
            }
          })
        }))
        this.$emit('close')
        this.loading = false
      } catch (e) {
        this.loading = false
        this.error = get(e, 'response.data') || e
        throw e
      }
    }
  },
  async created () {
    this.initialLoading = true
    try {
      await Promise.all([
        ...(this.machines.length ? [] : [this.findMachines()]),
        ...(this.alertNotifications.length ? [] : [this.findAlertNotifications()]),
        ...(this.alertDampenings.length ? [] : [this.findAlertDampening()]),
        ...(this.alertConditionTypes.length ? [] : [this.findAlertConditionTypes()])
      ])
      if (this.recordId) {
        let record = {
          all_machines: false,
          machines: [],
          schedule_days: ''
        }
        if (this.recordId !== 'new') {
          let conditionsResponse
          [record, conditionsResponse] = await Promise.all([
            this.getItem(this.recordId),
            axios({
              method: 'GET',
              url: '/rest/alerts/' + this.recordId + '/conditions'
            })
          ])
          this.conditions = get(conditionsResponse, 'data.rows')
          this.conditions = this.conditions.map(condition => {
            condition = { ...condition }
            if (this.typeForField(condition.property) === 'boolean') {
              if (['1', 1, true, 'true'].includes(condition.value)) {
                condition.value = '1'
              } else {
                condition.value = ''
              }
            }
            return condition
          })
        }
        this.record = cloneDeep(record)
        this.initialLoading = false
      } else {
        this.initialLoading = false
      }
      if (this.record.all_machines) {
        this.record.machines = this.machines.map(m => m.id)
      }
    } catch (e) {
      this.initialLoading = false
      throw e
    }
  }
}
</script>
